import {
  Button,
  Chip,
  Divider,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Pagination,
  Spinner,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs,
  useDisclosure,
} from "@nextui-org/react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import axios from "axios";
import { FiSearch, FiTrash } from "react-icons/fi";
import { MdOutlineFileDownload } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { columns } from "../data/quotesData";
import { refreshIfNeeded } from "../store/actions/refreshIfNeeded";
import { STATUS } from "../store/constants";

import { changeStatus } from "../store/slices/supplierSlice";
import { displayDate, formatTime } from "../utils";
const safeLower = (str) => (typeof str === "string" ? str.toLowerCase() : "");

const statusColorMap = {
  ["awaiting approval"]: "warning",
  ["in progress"]: "warning",
  accepted: "success",
  dispute: "danger",
  rejected: "danger",
  discarded: "danger",
  cancelled: "danger",
  ["Order created"]: "secondary",
};

const INITIAL_VISIBLE_COLUMNS = [
  "status",
  "industry",
  "company",
  "car",
  "part",
  "duration",
  "delete",
];

const Quotes = () => {
  const dispatch = useDispatch();
  const { status } = useSelector((state) => state.supplier);

  // UI / data state
  const [allQuotes, setAllQuotes] = useState([]); // concatenated backend batches
  const [filterValue, setFilterValue] = useState("");
  const [sortDescriptor, setSortDescriptor] = useState({ column: null, direction: null });

  let role = localStorage.getItem("role");
  const [visibleColumns, setVisibleColumns] = useState(() => {
    let cols = [...INITIAL_VISIBLE_COLUMNS];
    if (role === "CUSTOMER") cols = cols.filter((c) => c !== "delete");
    return new Set(cols);
  });

  const [statusFilter, setStatusFilter] = useState("all");
  const [rowsPerPage, setRowsPerPage] = useState(6);
const [searchTerm, setSearchTerm] = useState(""); // immediate input
 
  // UI pagination
  const [page, setPage] = useState(() => {
    const stored = localStorage.getItem("currentPage");
    return stored ? Number(stored) : 1;
  });

  // backend batching state
  const [batchSize] = useState(100);
  const [batchPage, setBatchPage] = useState(0);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const fetchingRef = useRef(false);

  // refs to hold latest values for stable callbacks
  const filterRef = useRef(filterValue);
  const sortRef = useRef(sortDescriptor);
  const allDataLoadedRef = useRef(allDataLoaded);
  const batchPageRef = useRef(batchPage);

  // keep refs in sync
  useEffect(() => {
    filterRef.current = filterValue;
  }, [filterValue]);
  useEffect(() => {
    sortRef.current = sortDescriptor;
  }, [sortDescriptor]);
  useEffect(() => {
    allDataLoadedRef.current = allDataLoaded;
  }, [allDataLoaded]);
  useEffect(() => {
    batchPageRef.current = batchPage;
  }, [batchPage]);

  // server totals
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // modal / delete
  const [deleteId, setDeleteId] = useState(null);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const showModal = (id) => {
    setDeleteId(id);
    onOpen();
  };

  /* ----------------
     debounce for input (useRef timer; stable)
     -----------------*/
    const searchTimerRef = useRef(null);

const onSearchChange = useCallback((value) => {
    setSearchTerm(value); // immediate UI update

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(() => {
      setFilterValue(value.trim());
      setPage(1);
      localStorage.setItem("currentPage", 1);
      searchTimerRef.current = null;
    }, 500);
  }, []);

const onClear = useCallback(() => {
  setSearchTerm("");
  if (searchTimerRef.current) {
clearTimeout(searchTimerRef.current);
  searchTimerRef.current = null;
  }
  setFilterValue("");
  setPage(1);
  localStorage.setItem("currentPage", 1);
}, []);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
    };
  }, []);

  /* ----------------
     stable fetchBatch: does NOT close over changing filter/sort directly
     pass optional params or default to refs
     -----------------*/
  const fetchBatch = useCallback(
    async (batchNum, { replace = false, filter, sort } = {}) => {
      // choose current filter/sort if not provided
      const curFilter = typeof filter === "undefined" ? filterRef.current : filter;
      const curSort = typeof sort === "undefined" ? sortRef.current : sort;

      // guard concurrent fetch
      if (fetchingRef.current) return;
      if (allDataLoadedRef.current && !replace) return;

      try {
        fetchingRef.current = true;
        dispatch(changeStatus(STATUS.LOADING));
        await dispatch(refreshIfNeeded());
        const fresh = localStorage.getItem("accessToken");
        const auth = { headers: { Authorization: `Bearer ${fresh}` } };

        const qp = new URLSearchParams();
        qp.append("page", batchNum);
        qp.append("pageSize", batchSize);
        if (curFilter) qp.append("search", curFilter);
        if (curSort?.column) {
          qp.append("sortBy", curSort.column);
          qp.append("sortOrder", curSort.direction === "ascending" ? "asc" : "desc");
        }

        // debug: console.log("fetching batch", batchNum, { search: curFilter, sort: curSort });

        const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/quotes/?${qp}`, auth);

        const fetchedRows = res.data?.details?.rows || [];
        const indexObj = res.data?.details?.index || {};
        const backendCurrentPage = indexObj.currentPage || batchNum;
        const backendTotalRecords = indexObj.totalRecords || 0;

        setTotalRecords(backendTotalRecords);
        setTotalPages(Math.ceil(backendTotalRecords / rowsPerPage));

        if (replace || batchNum === 1) {
          setAllQuotes(fetchedRows);
        } else {
          setAllQuotes((prev) => {
            const prevIds = new Set(prev.map((r) => r.id));
            const uniqueNew = fetchedRows.filter((r) => !prevIds.has(r.id));
            return [...prev, ...uniqueNew];
          });
        }

        setBatchPage(backendCurrentPage);
        if (fetchedRows.length < batchSize) {
          setAllDataLoaded(true);
        } else {
          setAllDataLoaded(false);
        }

        dispatch(changeStatus(STATUS.IDLE));
      } catch (err) {
        dispatch(changeStatus(STATUS.ERROR));
        console.error("fetchBatch error:", err?.response?.data || err.message || err);
      } finally {
        fetchingRef.current = false;
      }
    },
    [dispatch, batchSize, rowsPerPage]
  );

  /* ensurePageData now calls stable fetchBatch which reads filterRef */
  const ensurePageData = useCallback(
    async (uiPage) => {
      const requiredEndIndex = uiPage * rowsPerPage;
      if (requiredEndIndex <= allQuotes.length) return;
      if (allDataLoadedRef.current) return;

      const requiredBatch = Math.ceil(requiredEndIndex / batchSize);
      let startBatch = batchPageRef.current || 0;
      for (let b = startBatch + 1; b <= requiredBatch; b++) {
        if (fetchingRef.current) {
          await new Promise((res) => {
            const id = setInterval(() => {
              if (!fetchingRef.current) {
                clearInterval(id);
                res();
              }
            }, 50);
          });
        }
        await fetchBatch(b, { replace: false });
        if (allDataLoadedRef.current) break;
      }
    },
    [allQuotes.length, rowsPerPage, batchSize, fetchBatch]
  );

  const handlePageChange = useCallback(
    async (newPage) => {
      setPage(newPage);
      localStorage.setItem("currentPage", newPage);
      ensurePageData(newPage).catch((e) => console.error("ensurePageData error:", e));
    },
    [ensurePageData]
  );

  /* Reset / initial load when filter/sort/rowsPerPage change:
     - clear allQuotes, reset batch state and fetch batch 1 (replace)
     We intentionally keep this effect dependent on filterValue & sortDescriptor so it reacts after debounce.
  */
  useEffect(() => {
    setAllQuotes([]);
    setAllDataLoaded(false);
    setBatchPage(0);
    setPage(1);
    // fetch first backend batch with replace:true -- pass explicit filter/sort (safer)
    fetchBatch(1, { replace: true, filter: filterRef.current, sort: sortRef.current }).catch((e) =>
      console.error(e)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, sortDescriptor.column, sortDescriptor.direction, rowsPerPage]);

  /* periodic refresh: stable fetchBatch so effect won't unnecessarily re-create interval often */
  useEffect(() => {
    const id = setInterval(() => {
      if (!fetchingRef.current) fetchBatch(1, { replace: true }).catch(() => {});
    }, 120000);
    return () => clearInterval(id);
  }, [fetchBatch]);

  /* Derived lists (filter / sort / slice for table) */
  const filteredItems = useMemo(() => {
    let data = [...allQuotes];
    if (statusFilter !== "all") {
      data = data.filter(
        (q) => typeof q?.QuoteStatus?.status === "string" && q.QuoteStatus.status.toLowerCase() === statusFilter
      );
    }
    return data;
  }, [allQuotes, statusFilter]);

  const items = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredItems.slice(start, end);
  }, [filteredItems, page, rowsPerPage]);

  /* renderCell */
  const renderCell = useCallback((data, columnKey) => {
    const cellValue = data[columnKey];

    switch (columnKey) {
      case "status": {
        const rawStatus = data?.QuoteStatus?.status;
        const statusKey = typeof rawStatus === "string" ? rawStatus.toLowerCase() : "unknown";
        return (
          <Chip className="capitalize" color={statusColorMap[statusKey] ?? "default"} variant="flat">
            {rawStatus ?? "Unknown"}
          </Chip>
        );
      }

      case "company":
        return (
          role === "CUSTOMER" && (
            <div>
              <p className="table_cell">{data.User?.company_name || "Not Provided"}</p>
            </div>
          )
        );

      case "industry":
        return (
          role === "SUPPLIER" && (
            <div>
              <p className="table_cell">{data.Request?.User?.Industry || "Not Provided"}</p>
            </div>
          )
        );
      case "car":
        return (
          <p className="table_cell capitalize">
            {safeLower(data?.Request?.VehicleMake?.name || data?.Request?.custom_make)}{" "}
            {safeLower(data?.Request?.VehicleModel?.name || data?.Request?.custom_model)}
          </p>
        );

      case "part":
        return <p className="table_cell capitalize">{safeLower(data?.Request?.Part?.name || data?.Request?.custom_part_type)}</p>;

      case "duration":
        return (
          <div className="flex flex-col gap-1 ">
            <p className="table_cell capitalize">{formatTime(data?.createdAt || "Not Provided")}</p>
            <p className="table_cell_sm">{displayDate(data?.createdAt)}</p>
          </div>
        );
      case "delete":
        if (role === "CUSTOMER") return null;
        return (
          <div className="flex items-center gap-2 text-lg ">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                showModal(data.id);
              }}
            >
              <FiTrash className="text-red-500 cursor-pointer" />
            </button>
          </div>
        );

      default:
        return cellValue;
    }
  }, [role]);

  /* Delete handler */
  const handleDelete = async (id) => {
    try {
      await dispatch(refreshIfNeeded());
      const fresh = localStorage.getItem("accessToken");
      if (!fresh) {
        console.error("No access token found. Please login.");
        return;
      }
      const response = await axios.put(`${process.env.REACT_APP_SERVER_URL_V2}/quotes/deletequote/${id}`, {}, { headers: { Authorization: `Bearer ${fresh}` } });

      setAllQuotes((prev) => prev.filter((q) => q.id !== id));
      if (response.status === 200) {
        console.log("Quote deleted successfully:", id);
      } else {
        console.error("Failed to delete quote:", response);
      }
    } catch (error) {
      console.error("Error deleting quote:", error.response ? error.response.data : error.message);
    }
  };

  /* CSV export */
  const handleDownloadCSV = () => {
    const rowsToExport = filteredItems;
    const header = ["Quote ID", "Reference", "Status", "Industry", "Car", "Part", "Created At"];
    const lines = rowsToExport.map((r) => {
      const make = r.Request?.VehicleMake?.name || r.Request?.custom_make || "";
      const model = r.Request?.VehicleModel?.name || r.Request?.custom_model || "";
      const industry = r?.Request?.User?.Industry || "Not Provided";
      const created = new Date(r?.createdAt).toLocaleString();
      const part = r.Request?.Part?.name || r.Request?.custom_part_type || "";
      const cells = [r?.id || "Not Provided", r?.reference_id || "Not Provided", r?.QuoteStatus?.status || "Not Provided", industry, `${make} ${model}`, part, created];
      return cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",");
    });

    const csvContent = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "quotes_report.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onRowsPerPageChange = useCallback((e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
    localStorage.setItem("currentPage", 1);
  }, []);
 
 

//   const onClear = useCallback(() => {
//   setSearchTerm("");
//   setFilterValue("");
//   setPage(1);
//   localStorage.setItem("currentPage", 1);
// }, []);

  /* Header and Bottom content (unchanged except wiring) */
  const topContent = useMemo(() => {
    return (
      <div className="flex flex-col gap-6">
        <div className="w-full flex justify-between items-center gap-3">
          <Input isClearable className="flex-1 sm:max-w-[44%]" placeholder="Search Quotes" startContent={<FiSearch />}   value={searchTerm} onClear={() => onClear()} onValueChange={onSearchChange} />

          {role === "SUPPLIER" && (
            <Button startContent={<MdOutlineFileDownload />} onPress={handleDownloadCSV} disabled={status === STATUS.LOADING || filteredItems.length === 0} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm">
              {status === STATUS.LOADING ? "Loading…" : "Download CSV"}
            </Button>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row flex-wrap items-center justify-between gap-y-4">
          <div>
            <Tabs
              aria-label="Options"
              variant="underlined"
              selectedKey={statusFilter}
              onSelectionChange={(key) => {
                setStatusFilter(key);
                setPage(1);
              }}
              className="mobile-only:[&>div]:grid mobile-only:[&>div]:grid-cols-2 mobile-only:[&>div]:gap-2"
            >
              <Tab key="all" title="All Quotes"></Tab>
              <Tab key="awaiting approval" title="Awaiting Approval"></Tab>
              <Tab key="in progress" title="In Progress"></Tab>
              <Tab key="accepted" title="Quotes Accepted"></Tab>
            </Tabs>
          </div>

          <div className="flex items-center gap-5 text-xs sm:text-sm text-default-400">
            <span>Total: {totalRecords} Quotes</span>
            <Divider orientation="vertical" className="h-6" />
            <label className="flex items-center">
              Rows Per Page:
              <select className="bg-transparent outline-none" onChange={onRowsPerPageChange} value={rowsPerPage}>
                <option value="6">6</option>
                <option value="10">10</option>
                <option value="15">15</option>
              </select>
            </label>
          </div>
        </div>
      </div>
    );
  }, [searchTerm, onSearchChange, onClear, rowsPerPage, role, status, filteredItems.length, totalRecords]);

  const bottomContent = useMemo(() => {
    return (
      <div className="py-2 px-2 flex justify-center items-center">
        <Pagination key={`${page}-${totalPages}`} isCompact showControls showShadow color="primary" page={page} total={totalPages || 1} onChange={handlePageChange} />
      </div>
    );
  }, [page, totalPages, handlePageChange]);

  /* Table columns (unchanged) */
  const headerColumns = useMemo(() => {
    if (visibleColumns === "all") return columns;
    let column = [...columns];
    if (role === "SUPPLIER") column = column.filter((item) => item.uid !== "company");
    else if (role === "CUSTOMER") column = column.filter((item) => item.uid !== "industry");
    return column.filter((colum) => Array.from(visibleColumns).includes(colum.uid));
  }, [visibleColumns, role]);

  /* fix page bounds when filteredItems length shrinks */
  useEffect(() => {
    const pagesNow = Math.max(Math.ceil(filteredItems.length / rowsPerPage), 1);
    if (page > pagesNow && page !== 1) {
      setPage(pagesNow);
      localStorage.setItem("currentPage", pagesNow);
    }
  }, [filteredItems.length, rowsPerPage, page]);

  /* Table JSX */
  return (
    <>
      <Table
        aria-label="Quotes Table"
        isHeaderSticky
        isStriped
        topContent={topContent}
        topContentPlacement="outside"
        bottomContent={bottomContent}
        bottomContentPlacement="outside"
        sortDescriptor={sortDescriptor}
        onSortChange={(newSort) => {
          // set sort descriptor and reset page; effect will re-fetch batch 1
          setSortDescriptor(newSort);
          setPage(1);
          localStorage.setItem("currentPage", 1);
        }}
        classNames={{ td: "whitespace-nowrap" }}
      >
        <TableHeader columns={headerColumns}>
          {(column) => (
            <TableColumn key={column.uid} align={column.uid === "actions" ? "center" : "start"} allowsSorting={["status", "customer", "company", "car", "part", "delete", "duration"].includes(column.uid)}>
              {column.name}
            </TableColumn>
          )}
        </TableHeader>

        <TableBody emptyContent={"No Quotes Found"} items={items} isLoading={status === STATUS.LOADING && allQuotes.length === 0} loadingContent={<Spinner label="Loading..." className="w-full bg-white" />}>
          {(item) => (
            <TableRow key={item.id}>
              {(columnKey) => (
                <TableCell>
                  {role === "SUPPLIER" ? (
                    <Link to={`/quotes/${item.id}`}>{renderCell(item, columnKey)}</Link>
                  ) : role === "CUSTOMER" ? (
                    <Link to={`/updateStatus/${item.id}`}>{renderCell(item, columnKey)}</Link>
                  ) : (
                    renderCell(item, columnKey)
                  )}
                </TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Modal isOpen={isOpen} onOpenChange={onOpenChange} classNames={{ wrapper: "flex items-center justify-center min-h-screen" }}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Confirm Delete</ModalHeader>
              <ModalBody>Are You Sure You Want To Delete This Quote?</ModalBody>
              <ModalFooter>
                <Button
                  color="primary"
                  onPress={() => {
                    handleDelete(deleteId);
                    onClose();
                  }}
                >
                  Yes
                </Button>
                <Button variant="light" onPress={onClose}>
                  No
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default Quotes;
